"use client";

import type { ImportSeparatorType } from "@/utils/import/importSource";
import {
  availableProfiles,
  ImportProfileType,
} from "@/utils/import/profiles/importProfiles";
import {
  FileLevelErrorType,
  RowLevelErrorType,
} from "@/utils/import/validations/validationService";
import {
  ManageImportCandidatesResultType,
  ManageImportContractsResultType,
  type ImportCandidatesWarningType,
  type ImportUsageStatusType,
  type importMatchCountryStateType,
} from "@/utils/serverActions/import/importActions";
import React, { createContext, useContext, useState } from "react";

// "columns" matches the columns of the data to the fields of the profile.
export type ImportStep = "profile" | "upload" | "columns" | "preview" | "complete";

// The data to import, as given (pasted or read from a file) and how it is read.
export type ImportSourceType = {
  mode: "paste" | "file";
  /** Pasted text. */
  text: string;
  /** File chosen: its name, and its rows once read (an .xlsx, or a CSV split). */
  fileName: string | null;
  fileKind: "xlsx" | "csv" | null;
  /** Text of a CSV file, kept to split it again when its separator is changed. */
  fileText: string | null;
  fileRows: string[][] | null;
  /** Separator of a CSV file, detected and changeable. */
  separator: ImportSeparatorType;
  fileError: string | null;
  /** Whether the first row holds the names of the columns. */
  hasHeader: boolean;
  hasHeaderTouched: boolean;
  /** Field assigned to each column (profile key or null), once the user changed it. */
  mapping: Array<string | null> | null;
  isProcessing: boolean;
};

export const importSourceInitialState: ImportSourceType = {
  mode: "paste",
  text: "",
  fileName: null,
  fileKind: null,
  fileText: null,
  fileRows: null,
  separator: "	",
  fileError: null,
  hasHeader: false,
  hasHeaderTouched: false,
  mapping: null,
  isProcessing: false,
};

// Define the shape of your session object
export type importType = {
  fileErrors: FileLevelErrorType[];
  rowErrors: RowLevelErrorType[];
  // Candidates: warnings of the database about rows (they do not block the import)
  // and the rows whose warning the user accepted (row numbers, from 1).
  rowWarnings: ImportCandidatesWarningType[];
  forcedRows: number[];
  totalRecords: number;
  isImporting: boolean;
  open: boolean;
  isProcessing: boolean;
  selectedProfile: ImportProfileType;
  previewData: Record<string, unknown>[];
  currentStep: ImportStep;
  importType: "candidates" | "clients" | "contracts" | null;
  // Data to import (pasted or from a file) and its column matching
  source: ImportSourceType;
  // Resultados de importación
  importResults: ManageImportContractsResultType | null;
  // Result of the candidates import (the contracts one is importResults)
  candidatesResult: ManageImportCandidatesResultType | null;
  // Countries (and states) the rows name, as matched by the database: a corrected
  // row is validated again with them.
  countryStateData: importMatchCountryStateType[] | null;
  // What the plan allows this import and what the company has used, asked before
  // importing (candidates and contracts). allowed = false blocks the import.
  usage: ImportUsageStatusType | null;
};

export const importInitialState: importType = {
  fileErrors: [],
  rowErrors: [],
  rowWarnings: [],
  forcedRows: [],
  totalRecords: 0,
  isImporting: false,
  open: false,
  isProcessing: false,
  selectedProfile: availableProfiles[0],
  previewData: [],
  currentStep: "profile",
  importType: availableProfiles[0].id,
  source: importSourceInitialState,
  importResults: null,
  candidatesResult: null,
  countryStateData: null,
  usage: null,
};

// Update the type definition in your context
interface importContextType {
  importData: importType;
  setImportData: React.Dispatch<React.SetStateAction<importType>>;
}

const importContext = createContext<importContextType | undefined>(undefined);

export const ImportProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [importData, setImportData] = useState<importType>(
    importInitialState as importType
  );

  return (
    <importContext.Provider
      value={{
        importData,
        setImportData,
      }}
    >
      {children}
    </importContext.Provider>
  );
};

/**
 *
 * Use the scheduling context the has the selected cycle, contract and unit
 */
export const useImport = () => {
  const context = useContext(importContext);
  if (context === undefined) {
    throw new Error("useImport must be used within a Provider");
  }
  return context;
};
